
import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const supabaseClient = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface EnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  course: { codigo: string; titulo: string };
}

export default function EnrollmentModal({ isOpen, onClose, course }: EnrollmentModalProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    dni: '',
    fechaNacimiento: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    codigoPostal: '',
    provincia: '',
    contactoPreferido: 'email'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Si el usuario está autenticado, guardar en inscripciones_usuarios
      if (user) {
        const { error } = await supabase
          .from('inscripciones_usuarios')
          .insert({
            usuario_id: user.id,
            curso_codigo: course.codigo,
            curso_titulo: course.titulo,
            estado: 'activo',
            progreso: 0
          });

        if (error) throw error;

        setSubmitStatus('success');
        setFormData({
          nombre: '',
          apellidos: '',
          dni: '',
          fechaNacimiento: '',
          email: '',
          telefono: '',
          direccion: '',
          ciudad: '',
          codigoPostal: '',
          provincia: '',
          contactoPreferido: 'email'
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
          // Redirigir al dashboard
          window.REACT_APP_NAVIGATE('/mi-cuenta');
        }, 2000);
      } else {
        // Si no está autenticado, usar el formulario tradicional
        const formDataToSend = new URLSearchParams();
        formDataToSend.append('nombre', formData.nombre);
        formDataToSend.append('apellidos', formData.apellidos);
        formDataToSend.append('dni', formData.dni);
        formDataToSend.append('fecha_nacimiento', formData.fechaNacimiento);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('telefono', formData.telefono);
        formDataToSend.append('contacto_preferido', formData.contactoPreferido);
        formDataToSend.append('direccion', formData.direccion);
        formDataToSend.append('ciudad', formData.ciudad);
        formDataToSend.append('codigo_postal', formData.codigoPostal);
        formDataToSend.append('provincia', formData.provincia);
        formDataToSend.append('curso', course.titulo);
        formDataToSend.append('codigo_curso', course.codigo);

        const response = await fetch(
          'https://readdy.ai/api/public/form/submit/1641cce445e23a33732a9af05560332a/inscripcion-curso',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formDataToSend.toString(),
          }
        );

        if (!response.ok) {
          throw new Error('Error al enviar el formulario');
        }

        setSubmitStatus('success');
        setFormData({
          nombre: '',
          apellidos: '',
          dni: '',
          fechaNacimiento: '',
          email: '',
          telefono: '',
          direccion: '',
          ciudad: '',
          codigoPostal: '',
          provincia: '',
          contactoPreferido: 'email'
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-teal-600 text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Formulario de Inscripción</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors cursor-pointer"
            type="button"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
        </div>

        {user ? (
          <div className="p-6">
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-2xl text-cyan-600 mt-1"></i>
                <div>
                  <h3 className="font-semibold text-cyan-900 mb-1">Inscripción Rápida</h3>
                  <p className="text-sm text-cyan-800">
                    Como usuario registrado, tu inscripción se procesará automáticamente y podrás acceder al curso desde tu área de alumno.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-cyan-500 text-white py-4 rounded-lg font-semibold hover:bg-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Procesando...' : 'Confirmar Inscripción'}
            </button>
          </div>
        ) : (
          <>
            <form id="enrollment-form" onSubmit={handleSubmit} className="p-6 space-y-6">
              {course.titulo && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-sm text-teal-800">
                    <strong>Curso seleccionado:</strong> {course.titulo}
                  </p>
                </div>
              )}

              {/* Datos Personales */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-user-line mr-2 text-teal-600"></i>
                  Datos Personales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label htmlFor="apellidos" className="block text-sm font-medium text-gray-700 mb-1">
                      Apellidos *
                    </label>
                    <input
                      type="text"
                      id="apellidos"
                      name="apellidos"
                      value={formData.apellidos}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Tus apellidos"
                    />
                  </div>

                  <div>
                    <label htmlFor="dni" className="block text-sm font-medium text-gray-700 mb-1">
                      DNI/NIE *
                    </label>
                    <input
                      type="text"
                      id="dni"
                      name="dni"
                      value={formData.dni}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="12345678A"
                    />
                  </div>

                  <div>
                    <label htmlFor="fechaNacimiento" className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Nacimiento *
                    </label>
                    <input
                      type="date"
                      id="fechaNacimiento"
                      name="fechaNacimiento"
                      value={formData.fechaNacimiento}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Datos de Contacto */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-phone-line mr-2 text-teal-600"></i>
                  Datos de Contacto
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Correo Electrónico *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="600 123 456"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="contactoPreferido" className="block text-sm font-medium text-gray-700 mb-1">
                      Forma de Contacto Preferida *
                    </label>
                    <select
                      id="contactoPreferido"
                      name="contactoPreferido"
                      value={formData.contactoPreferido}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="email">Correo Electrónico</option>
                      <option value="telefono">Teléfono</option>
                      <option value="whatsapp">WhatsApp</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Dirección */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-map-pin-line mr-2 text-teal-600"></i>
                  Dirección
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label htmlFor="direccion" className="block text-sm font-medium text-gray-700 mb-1">
                      Dirección Completa *
                    </label>
                    <input
                      type="text"
                      id="direccion"
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Calle, número, piso, puerta"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="ciudad" className="block text-sm font-medium text-gray-700 mb-1">
                        Ciudad *
                      </label>
                      <input
                        type="text"
                        id="ciudad"
                        name="ciudad"
                        value={formData.ciudad}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Tu ciudad"
                      />
                    </div>

                    <div>
                      <label htmlFor="codigoPostal" className="block text-sm font-medium text-gray-700 mb-1">
                        Código Postal *
                      </label>
                      <input
                        type="text"
                        id="codigoPostal"
                        name="codigoPostal"
                        value={formData.codigoPostal}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="28001"
                      />
                    </div>

                    <div>
                      <label htmlFor="provincia" className="block text-sm font-medium text-gray-700 mb-1">
                        Provincia *
                      </label>
                      <input
                        type="text"
                        id="provincia"
                        name="provincia"
                        value={formData.provincia}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Tu provincia"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Estado de envío */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center">
                  <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                  <span className="text-sm font-medium">¡Inscripción guardada con éxito! Nos pondremos en contacto contigo pronto.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
                  <i className="ri-error-warning-line text-xl mr-2"></i>
                  <span className="text-sm font-medium">Hubo un error al guardar la inscripción. Por favor, inténtalo de nuevo.</span>
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Guardando...' : 'Enviar Inscripción'}
                </button>
              </div>
            </form>

            <div className="px-6 pb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 text-center">
                  ¿Ya tienes cuenta?{' '}
                  <a href="/login" className="text-cyan-600 font-semibold hover:text-cyan-700">
                    Inicia sesión
                  </a>
                  {' '}para una inscripción más rápida
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
